<template>
  <ModalDialog
    header-text="Add Ingredients"
    contentClass="copy-modal-content"
    :canBeClosed="!copyInProgress"
    @closeModal="closeModal"
    open
  >
    <template v-slot:content>
      <IngredientPlantsGrid :plants="plantsWithSelection" />
      <Button
        type="button"
        theme-color="primary"
        :disabled="!anyPlantSelected || copyInProgress"
        @click="addToCartClicked"
        data-cy="submit-button"
        >Add to Cart</Button
      >
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import type { IdAndName } from "@/Domain/IdAndName";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import IngredientPlantsGrid from "./IngredientPlantsGrid.vue";
import type { PlantWithSelection } from "@/api/scheduleB";
import { computed, ref } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useScheduleAStore } from "@/stores/useScheduleAStore";

const main = useMainStore();
const scheduleStore = useScheduleAStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  plants: IdAndName[];
  ingredientIds: number[] | string[];
}>();

const emit = defineEmits<{
  (e: "closeModal", cartCount: number): void;
}>();

const copyInProgress = ref(false);
const copyCompleted = ref(false);

const plantsWithSelection = computed(() => {
  return props.plants as PlantWithSelection[];
});

const anyPlantSelected = computed(() => {
  return plantsWithSelection.value.some((p) => p.selected);
});

const addToCopyCartAsync = async function () {
  if (copyInProgress.value) return;

  copyInProgress.value = true;

  await handleResponsePromiseAsync(
    api.get("CopyIngredientsToCart", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      ingredientIds: props.ingredientIds.join(";"),
      ownsId: plantsWithSelection.value
        .filter((p) => p.selected)
        .map((p) => p.ID)
        .join(";"),
    }),
    (data) => {
      const cartCount = data.Item1;
      const status = data.Item2;
      copyCompleted.value = true;
      closeModal(true, cartCount, status);
    },
    "CopyIngredientsModal.vue",
    "addToCopyCartAsync",
    (error) => {
      console.log(error);
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Failed to add ingredients to the cart",
        duration: 5000,
      });
    }
  );

  copyInProgress.value = false;
};

const addToCartClicked = function () {
  addToCopyCartAsync();
};

const closeModal = function (
  closed: boolean,
  cartCount: number = -1,
  statusMessage: string = ""
) {
  if (!closed) {
    notificationStore.notify({
      type: NotificationTypeEnum.info,
      message: "Please wait while ingredients are being added to the cart",
      duration: 5000,
    });
    return;
  }

  if (copyCompleted.value) {
    notificationStore.notify({
      type:
        cartCount >= 0
          ? NotificationTypeEnum.success
          : NotificationTypeEnum.error,
      message:
        cartCount >= 0
          ? statusMessage ??
            `Ingredients added to the cart successfully. ${cartCount} ingredients in the cart`
          : statusMessage ?? "Failed to add ingredients to the cart",
      duration: 5000,
    });
  }

  if (cartCount > 0) {
    scheduleStore.setCartCount(SubApplicationType.Add, cartCount);
  }
  emit("closeModal", cartCount);
};
</script>

<style lang="scss">
.copy-modal-content {
  max-width: 600px;
  padding: 12px;

  .plant-grid {
    max-height: 360px;
    margin-bottom: 20px;
  }
}
</style>
